<template>
  <div class="w-full">
    <PresetFieldsTooltip
      :tooltip-data="selectedPresetTooltipData"
      placement="right"
    >
      <BittsSelect
        ref="selectRef"
        v-model="selectedPresetId"
        :class="selectClasses"
        :dropdown-class-name="dropdownClassName"
        :searchable="false"
        :has-opt-groups="true"
        :dropdown-match-select-width="false"
        :dropdown-button="true"
        :options="fixedPresetOptions"
        :disabled="disabled"
        :aria-label="ariaLabel"
        @change="handleChange"
        @dropdown-visible-change="handleDropdownVisibleChange"
      >
        <template #dropdownButtonTop>
          <div class="c-sharing-settings-preset-selector__header">
            <span class="text-neutral-text-strong font-bold">
              Choose preset to share
            </span>
            <BittsButton
              variant="outline"
              type="neutral"
              size="x-small"
              text="Manage"
              @click="handleManageClick"
            />
          </div>
        </template>
        <template #option="{ option }">
          <template v-if="isFooterItem(option)">
            <div class="c-sharing-settings-preset-selector__hidden-option" />
            <span>{{ option.label }}</span>
          </template>
          <PresetFieldsTooltip v-else :tooltip-data="getTooltipData(option)">
            <PresetOptionContent
              :option="option"
              :action-icon="getPresetSuffixIcon(option.value)"
              :is-selected="option.value === selectedPresetId"
              @action-click="
                (e: Event) => handleOpenPresetModal(e, option.value)
              "
            />
          </PresetFieldsTooltip>
        </template>
        <template v-if="customItemForBottomSlot" #dropdownButtonBottom>
          <div
            class="c-sharing-settings-preset-selector__footer"
            @mousedown.prevent
          >
            <PresetOptionContent
              :option="customItemForBottomSlot"
              :action-icon="
                getPresetSuffixIcon(customItemForBottomSlot.value as number)
              "
              :is-selected="customItemForBottomSlot.value === selectedPresetId"
              @action-click="
                (e: Event) =>
                  handleOpenPresetModal(
                    e,
                    customItemForBottomSlot!.value as number,
                  )
              "
              @click="handleCustomItemClick"
            />
          </div>
        </template>
      </BittsSelect>
    </PresetFieldsTooltip>
  </div>
</template>

<script setup lang="ts">
import { BittsButton, BittsSelect } from '@crossbeam/bitts';
import { BittsIconPrefix } from '@crossbeam/bitts/src/types';

import { computed, nextTick, ref, watch } from 'vue';
import { useRouter } from 'vue-router';

import {
  type PresetFieldsDataMap,
  usePresetFieldsData,
} from '@/composables/dataSharingHub/usePresetFieldsData';
import { CROSSBEAM_DEFAULT_PRESET_TYPE } from '@/constants/data_shares';
import { TEMP_TEAM_DEFAULT_PRESET } from '@/constants/feature_flags';
import { useFeatureFlagStore } from '@/stores';
import type {
  PresetOptionItem,
  PresetOptionsGroup,
} from '@/types/data_share_presets';
import { sortObjectsByMdmType } from '@/utils';

import PresetFieldsTooltip from './PresetFieldsTooltip.vue';
import PresetOptionContent from './PresetOptionContent.vue';

const props = withDefaults(
  defineProps<{
    initialSelectedPresetId: number | null;
    disabled?: boolean;
    canEditPresets?: boolean;
    presetOptions: PresetOptionsGroup[];
    feedId?: number;
    sourceId?: number;
    showTooltipOnSelected?: boolean;
    isCompact?: boolean;
    externalPresetFieldsData?: PresetFieldsDataMap;
    ariaLabel?: string;
  }>(),
  {
    disabled: false,
    canEditPresets: undefined,
    feedId: undefined,
    sourceId: undefined,
    showTooltipOnSelected: false,
    isCompact: false,
    externalPresetFieldsData: undefined,
    ariaLabel: undefined,
  },
);

const emit = defineEmits<{
  change: [presetId: number];
  'open-preset-modal': [presetId: number | undefined];
}>();

const router = useRouter();
const { hasFeatureFlag } = useFeatureFlagStore();
const { presetFieldsData: composablePresetFieldsData } = usePresetFieldsData();

const selectedPresetId = ref<number | null>(props.initialSelectedPresetId);
const selectRef = ref<InstanceType<typeof BittsSelect> | null>(null);
const isDropdownOpen = ref(false);

const selectedPresetTooltipData = computed(() => {
  if (
    !props.showTooltipOnSelected ||
    !selectedPresetId.value ||
    isDropdownOpen.value
  )
    return null;
  return getTooltipData({ value: selectedPresetId.value } as PresetOptionItem);
});

const selectClasses = computed(() => [
  'c-sharing-settings-preset-selector__preset',
  'c-sharing-settings-preset-selector__preset--ff-enabled',
  {
    'w-full': !props.isCompact,
    'c-sharing-settings-preset-selector__preset--compact': props.isCompact,
  },
]);

const dropdownClassName = computed(() => {
  const baseClass = 'c-sharing-settings-preset-selector__preset-dropdown';
  return `${baseClass} ${baseClass}--ff-enabled`;
});

const presetFieldsData = computed<PresetFieldsDataMap>(() => {
  if (props.externalPresetFieldsData) return props.externalPresetFieldsData;
  return composablePresetFieldsData.value;
});

function findSpecialItems(presetOptions: PresetOptionsGroup[]) {
  let singleSharePreset: PresetOptionItem | undefined;

  for (const group of presetOptions) {
    for (const item of group.items) {
      if (item.presetType === 'single_share_preset') {
        singleSharePreset = item;
      }
    }
  }

  return { singleSharePreset };
}

function stripSpecialItems(
  presetOptions: PresetOptionsGroup[],
): PresetOptionsGroup[] {
  return presetOptions
    .map((group) => ({
      ...group,
      items: group.items.filter(
        (item) =>
          item.presetType !== 'single_share_preset' && item.value !== 'custom',
      ),
    }))
    .filter((group) => group.items.length > 0);
}

const hasNamedPresets = computed(() =>
  stripSpecialItems(props.presetOptions)
    .flatMap((group) => group.items)
    .some((item) => item.presetType !== CROSSBEAM_DEFAULT_PRESET_TYPE),
);

const customItemForBottomSlot = computed<PresetOptionItem | null>(() => {
  const { singleSharePreset } = findSpecialItems(props.presetOptions);

  if (singleSharePreset) {
    return { ...singleSharePreset, label: 'Custom Field Selection' };
  }

  return {
    value: 'custom',
    label: 'Customize Fields',
  } as PresetOptionItem;
});

const fixedPresetOptions = computed((): PresetOptionsGroup[] => {
  const regularGroups = stripSpecialItems(props.presetOptions);
  const items = regularGroups.flatMap((group) => group.items);

  const { singleSharePreset } = findSpecialItems(props.presetOptions);
  if (singleSharePreset) {
    items.push({ ...singleSharePreset, label: 'Custom Field Selection' });
  }

  return [{ heading: null, items }];
});

watch(
  () => props.initialSelectedPresetId,
  (newValue) => {
    selectedPresetId.value = newValue;
  },
);

function getTooltipData(option: PresetOptionItem):
  | {
      tableName: string;
      mdm_type: string;
      fields: { id: number; display_name: string }[];
    }[]
  | null {
  const presetData = presetFieldsData.value[option.value as number];
  if (!presetData) return null;

  const sourcesArray = Object.keys(presetData.sourcesAndFields).map(
    (tableName) => ({
      tableName,
      mdm_type: presetData.sourcesAndFields[tableName]?.mdm_type || '',
      fields: presetData.sourcesAndFields[tableName]?.fields || [],
    }),
  );

  if (sourcesArray.length === 0) return null;

  return sortObjectsByMdmType(sourcesArray);
}

function handleChange(presetId: number | 'custom') {
  if (presetId === 'custom') {
    selectedPresetId.value = props.initialSelectedPresetId;
    emit('open-preset-modal', undefined);
    return;
  }

  emit('change', presetId);
}

function isFooterItem(option: PresetOptionItem): boolean {
  const footer = customItemForBottomSlot.value;
  return !!footer && option.value === footer.value;
}

function handleCustomItemClick() {
  const item = customItemForBottomSlot.value;
  if (!item) return;

  selectRef.value?.instantBlur();

  if (item.presetType === 'single_share_preset') {
    handleChange(item.value as number);
  } else {
    handleChange('custom');
  }
}

function handleDropdownVisibleChange(visible: boolean) {
  isDropdownOpen.value = visible;
}

function handleManageClick() {
  selectRef.value?.instantBlur();

  if (props.feedId) {
    const baseUrl = `/data-sources/feed/${props.feedId}/settings/field-presets`;
    const url = props.sourceId ? `${baseUrl}/${props.sourceId}` : baseUrl;
    router.push(url);
  }
}

function getPresetSuffixIcon(
  presetId?: number,
): [BittsIconPrefix, string] | null {
  if (!presetId || typeof presetId !== 'number') return null;

  if (props.disabled) {
    return ['far', 'eye'] as [BittsIconPrefix, string];
  }

  let presetOption: PresetOptionItem | undefined;

  for (const group of props.presetOptions) {
    presetOption = group.items.find((item) => item.value === presetId);
    if (presetOption) break;
  }

  const isSingleSharePreset =
    presetOption?.presetType === 'single_share_preset';

  const isCrossbeamPreset =
    presetOption?.presetType === CROSSBEAM_DEFAULT_PRESET_TYPE;

  const canEdit =
    props.canEditPresets === undefined ? true : props.canEditPresets;

  // The Crossbeam preset's pencil opens the read-only view, so anyone gets it,
  // but only once a named preset has taken over the default.
  const shouldShowPencil = hasFeatureFlag(TEMP_TEAM_DEFAULT_PRESET)
    ? isCrossbeamPreset
      ? hasNamedPresets.value && !presetOption?.isDefault
      : isSingleSharePreset || canEdit
    : (isSingleSharePreset || canEdit) && !isCrossbeamPreset;

  if (shouldShowPencil) {
    return ['fak', 'edit'] as [BittsIconPrefix, string];
  }

  return null;
}

async function handleOpenPresetModal(
  event: Event,
  presetId: number | undefined,
) {
  event.stopPropagation();
  event.preventDefault();

  try {
    selectRef.value?.instantBlur();
  } catch {
    await nextTick();
    (document.activeElement as HTMLElement)?.blur();
  }

  emit('open-preset-modal', presetId);
}
</script>

<style lang="pcss" scoped>
@reference "../../css/theme.css";

.bitts-select :deep(.ant-select-selection-item) {
  .c-bitts-btn {
    @apply hidden;
  }
  .c-bitts-select__option-icon {
    @apply hidden;
  }
  .bitts-tag {
    @apply hidden!;
  }
  .bitts-tooltip {
    @apply pointer-events-none;

    .ant-tooltip {
      @apply hidden!;
    }
  }
}

.c-sharing-settings-preset-selector__preset {
  @apply min-w-[80px] max-w-[240px];
}

/* Force truncation within container */
.c-sharing-settings-preset-selector__preset :deep(.ant-select) {
  @apply w-full max-w-full;
}

.c-sharing-settings-preset-selector__preset :deep(.ant-select-selector) {
  @apply max-w-full flex items-center;
}

.c-sharing-settings-preset-selector__preset
  :deep(.ant-select-selection-search) {
  @apply flex items-center;
}

.c-sharing-settings-preset-selector__preset
  :deep(.ant-select-selection-placeholder) {
  @apply flex items-center;
}

.c-sharing-settings-preset-selector__preset :deep(.ant-select-selection-item) {
  @apply overflow-hidden! whitespace-nowrap! max-w-full! min-w-0! flex! items-center!;
  text-overflow: ellipsis !important;
  flex: 1 1 auto !important;
}

/* Ensure all child elements also truncate */
.c-sharing-settings-preset-selector__preset
  :deep(.ant-select-selection-item > *) {
  @apply overflow-hidden! whitespace-nowrap! max-w-full! min-w-0!;
  text-overflow: ellipsis !important;
}

.c-sharing-settings-preset-selector__preset--ff-enabled.c-sharing-settings-preset-selector__preset--ff-enabled
  :deep(.ant-select:not(.ant-select-customize-input) .ant-select-selector) {
  border: 1px solid !important;
  @apply h-28 border-neutral-border! rounded-[6px]!;
}

.c-sharing-settings-preset-selector__preset--ff-enabled.c-sharing-settings-preset-selector__preset--ff-enabled
  :deep(
    .ant-select-focused:not(.ant-select-customize-input) .ant-select-selector
  ) {
  border: 1px solid !important;
  @apply border-primary-border! shadow-sm! rounded-[6px]!;
}

.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--compact {
  @apply w-auto max-w-full;
}

.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--ff-enabled {
  @apply max-w-[200px];
}

.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--compact
  :deep(.ant-select) {
  @apply w-auto max-w-full;
}

.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--ff-enabled
  :deep(.ant-select) {
  @apply max-w-[200px];
}

.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--compact
  :deep(.ant-select:not(.ant-select-customize-input) .ant-select-selector) {
  height: 24px !important;
  min-height: 24px !important;
  @apply max-w-full;
}

.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--compact
  :deep(.ant-select-selection-item) {
  line-height: 22px !important;
  @apply overflow-hidden! whitespace-nowrap! text-ellipsis!;
}

.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--compact
  :deep(.ant-select-selection-search) {
  @apply flex! items-center!;
}

.c-sharing-settings-preset-selector__preset--compact.c-sharing-settings-preset-selector__preset--compact
  :deep(.ant-select-arrow) {
  @apply flex! items-center!;
}

.c-sharing-settings-preset-selector__header {
  @apply flex items-center justify-between p-8 pt-4 pl-10 border-b border-neutral-border;
}

.c-sharing-settings-preset-selector__footer {
  @apply border-t border-neutral-border pt-6 pb-4 px-6 cursor-pointer;

  & > div {
    @apply px-8 py-4 rounded-4 h-[32px] flex items-center;
  }

  &:hover > div {
    @apply bg-neutral-background-weak;
  }
}
</style>

<style lang="pcss">
@reference "../../css/theme.css";

/* Dropdown styles need to be global since dropdown is rendered in a portal */
.c-sharing-settings-preset-selector__preset-dropdown {
  @apply min-w-[400px]!;
}

.c-sharing-settings-preset-selector__preset-dropdown .rc-virtual-list-holder {
  @apply max-h-[256px]!;
}

/* Uses custom #option slot */
.c-sharing-settings-preset-selector__preset-dropdown--ff-enabled {
  .ant-select-item.ant-select-item-group {
    @apply p-0 mb-2;
  }

  .ant-select-item.ant-select-item-group > div:first-child {
    @apply m-0! p-0!;
  }

  .ant-select-item {
    @apply mx-6 mb-2 px-8 py-4;
  }

  .ant-select-item.ant-select-item-option {
    @apply flex items-center h-[32px];
  }

  .ant-select-item:last-child {
    @apply mb-4;
  }

  .ant-select-item .ant-select-item-option-content {
    @apply ml-0;
  }

  .c-bitts-btn--ghost {
    @apply bg-transparent;
  }

  /* Hover state for options — suppress when footer is hovered */
  .ant-select-item-option-active:not(.ant-select-item-option-selected) {
    background-color: theme(colors.neutral.background-weak) !important;
  }

  &:has(.c-sharing-settings-preset-selector__footer:hover)
    .ant-select-item-option-active:not(.ant-select-item-option-selected) {
    background-color: transparent !important;
  }

  /* Selected option background */
  .ant-select-item-option-selected {
    background-color: theme(colors.secondary.background-weak) !important;
  }

  /* The option draws its own check, left of the pencil */
  .ant-select-item-option-state {
    @apply hidden;
  }

  /* Cancels the pencil button's padding, so the icon sits flush right and
     8px from its neighbour like the tag and the check */
  .ant-select-item .c-bitts-btn--ghost {
    @apply mx-[-10px];
  }

  /* BittsTag adds its own right margin on top of the row's 8px gap */
  .ant-select-item .bitts-tag .ant-tag {
    @apply mr-0;
  }

  .ant-select-item:has(.c-sharing-settings-preset-selector__hidden-option) {
    @apply hidden;
  }
}

/* Hide custom icon (wrench/plus), check, edit button, and recommended tag in selected value display, only show in dropdown */
.c-sharing-settings-preset-selector__preset {
  .ant-select-selection-item .c-sharing-settings-preset-selector__custom-icon,
  .ant-select-selection-item .c-sharing-settings-preset-selector__check,
  .ant-select-selection-item .c-bitts-btn,
  .ant-select-selection-item .c-bitts-tag,
  .ant-select-selection-item .fa-wrench {
    @apply hidden;
  }
}
</style>
